import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  Observable,
  gql,
} from "@apollo/client";
import { InMemoryCache, defaultDataIdFromObject } from "@apollo/client/cache";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import ActionCable from "actioncable";
import { ActionCableLink } from "graphql-ruby-client";

import nanoid from "nanoid";

import { GET_NOTIFICATIONS } from "./hooks/useGetNotifications";
import { GET_CREDENTIAL } from "./graphql/hooks/queries/useGetCredential";

const cache = new InMemoryCache({
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case "Credential":
        return object.accessToken;
      default:
        return defaultDataIdFromObject(object);
    }
  },
});

const cable = ActionCable.createConsumer("ws://localhost:3000/cable");

const hasSubscriptionOperation = ({ query: { definitions } }) => {
  return definitions.some(
    ({ kind, operation }) =>
      kind === "OperationDefinition" && operation === "subscription"
  );
};

const request = async (operation) => {
  const credentialsJSON = localStorage.getItem("credential");

  if (credentialsJSON) {
    try {
      const { accessToken, client, expiry, tokenType, uid } = JSON.parse(
        credentialsJSON
      );

      operation.setContext({
        headers: {
          accessToken,
          tokenType,
          client,
          expiry,
          uid,
        },
      });
    } catch {
      localStorage.removeItem("credential");
    }
  }
};

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle;
      Promise.resolve(operation)
        .then((operation) => request(operation))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: (...args) => {
              observer.complete(...args);
            },
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    })
);

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 10000,
    jitter: true,
  },
  attempts: {
    max: Infinity,
    retryIf: (error) => {
      return !!error.message.match(/Network/);
    },
  },
});

const httpLink = new HttpLink({
  uri: "/graphql",
  credentials: "include",
});

const client = new ApolloClient({
  typeDefs: gql`
    type Notification {
      body: String
      id: ID!
      status: String!
      title: String!
    }
    type Credential {
      accessToken: String!
      uid: String!
      tokenType: String!
      client: String!
      expiry: Integer!
    }
  `,
  resolvers: {
    Query: {
      notifications: () => [],
      credential: () => {
        const credentialsJSON = localStorage.getItem("credential");

        if (credentialsJSON) {
          return { __typename: "Credential", ...JSON.parse(credentialsJSON) };
        }
        return null;
      },
    },
    Mutation: {
      setCredential: async (_, credential, { cache }) => {
        localStorage.setItem("credential", JSON.stringify(credential));

        cache.writeQuery({
          query: GET_CREDENTIAL,
          data: { credential: { __typename: "Credential", ...credential } },
        });
        return null;
      },
      deleteCredential: async (_, _params, { cache }) => {
        localStorage.removeItem("credential");

        cache.writeQuery({
          query: GET_CREDENTIAL,
          data: { credential: null },
        });
        return null;
      },
      addNotification: async (_, { title, body = "", status }, { cache }) => {
        const { notifications } = await cache.readQuery({
          query: GET_NOTIFICATIONS,
        });

        cache.writeQuery({
          query: GET_NOTIFICATIONS,
          data: {
            notifications: [
              ...notifications,
              {
                __typename: "Notification",
                id: nanoid(),
                body,
                title,
                status,
              },
            ],
          },
        });
      },
      deleteNotification: async (_, { id }, { cache }) => {
        const { notifications } = await cache.readQuery({
          query: GET_NOTIFICATIONS,
        });

        cache.writeQuery({
          query: GET_NOTIFICATIONS,
          data: {
            notifications: notifications.filter(
              (notification) => notification.id !== id
            ),
          },
        });
      },
    },
  },
  link: ApolloLink.split(
    hasSubscriptionOperation,
    new ActionCableLink({ cable }),
    ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.map(({ message, locations, path }) => {
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
          });
        }
        if (networkError) {
          console.log(`[Network error]: ${networkError}`);
        }
      }),
      requestLink,
      retryLink,
      httpLink,
    ])
  ),
  cache,
});

cache.writeQuery({
  query: GET_NOTIFICATIONS,
  data: { notifications: [] },
});

export default client;
